import { useMemo, useState, type FormEvent } from "react";
import { useEditorStore, type UtilityCreate } from "../app/store";
import { Icon } from "../components/Icon";
import { cableEndpoints, type UtilityNode, type UtilityPort } from "../domain/patch";
import { defaultUtilityPosition, utilityNodes } from "./workspace-ports";

type UtilityKind =
  | "passive-mult"
  | "splitter-cable"
  | "stackable-junction"
  | "standalone-attenuator"
  | "inline-attenuator";
type MultPreset = "1-to-3" | "1-to-4" | "2x-1-to-3";
type SplitterPreset = "two-branch" | "three-branch";

function multPorts(preset: MultPreset): UtilityPort[] {
  if (preset === "2x-1-to-3") {
    return ["A", "B"].flatMap((groupId, row) =>
      [0, 1, 2, 3].map((index) => ({
        id: `${groupId.toLowerCase()}-${index + 1}`,
        label: `${groupId}${index + 1}`,
        groupId,
        position: { x: 34 + index * 50, y: 66 + row * 68 },
        direction: "bidirectional" as const,
        signalType: "mixed" as const,
        connectorType: "3.5mm-ts" as const,
      })),
    );
  }
  const count = preset === "1-to-4" ? 5 : 4;
  const spacing = count === 5 ? 40 : 50;
  const start = count === 5 ? 30 : 35;
  return Array.from({ length: count }, (_, index) => ({
    id: `a-${index + 1}`,
    label: `A${index + 1}`,
    groupId: "A",
    position: { x: start + index * spacing, y: 72 },
    direction: "bidirectional" as const,
    signalType: "mixed" as const,
    connectorType: "3.5mm-ts" as const,
  }));
}

function commonUtilityPorts(
  groupId: string,
  ports: Array<{ id: string; label: string; position: { x: number; y: number } }>,
): UtilityPort[] {
  return ports.map((port) => ({
    ...port,
    groupId,
    direction: "bidirectional",
    signalType: "mixed",
    connectorType: "3.5mm-ts",
  }));
}

function splitterPorts(preset: SplitterPreset): UtilityPort[] {
  const branches =
    preset === "three-branch"
      ? [
          { id: "branch-a", label: "A", position: { x: 186, y: 44 } },
          { id: "branch-b", label: "B", position: { x: 186, y: 78 } },
          { id: "branch-c", label: "C", position: { x: 186, y: 112 } },
        ]
      : [
          { id: "branch-a", label: "A", position: { x: 186, y: 52 } },
          { id: "branch-b", label: "B", position: { x: 186, y: 104 } },
        ];
  return commonUtilityPorts("split", [
    { id: "trunk", label: "TRUNK", position: { x: 34, y: 78 } },
    ...branches,
  ]);
}

function junctionPorts(): UtilityPort[] {
  return commonUtilityPorts("junction", [
    { id: "west", label: "W", position: { x: 34, y: 80 } },
    { id: "north", label: "N", position: { x: 110, y: 45 } },
    { id: "east", label: "E", position: { x: 186, y: 80 } },
    { id: "south", label: "S", position: { x: 110, y: 115 } },
  ]);
}

function attenuatorPorts(kind: "standalone-attenuator" | "inline-attenuator"): UtilityPort[] {
  const inline = kind === "inline-attenuator";
  return [
    {
      id: "input",
      label: "IN",
      groupId: "signal",
      position: { x: inline ? 32 : 46, y: inline ? 76 : 92 },
      direction: "input",
      signalType: "mixed",
      connectorType: "3.5mm-ts",
    },
    {
      id: "output",
      label: "OUT",
      groupId: "signal",
      position: { x: inline ? 148 : 174, y: inline ? 76 : 92 },
      direction: "output",
      signalType: "mixed",
      connectorType: "3.5mm-ts",
    },
  ];
}

function defaultName(kind: UtilityKind) {
  if (kind === "standalone-attenuator") return "Attenuator";
  if (kind === "inline-attenuator") return "Inline attenuator";
  if (kind === "splitter-cable") return "Splitter cable";
  if (kind === "stackable-junction") return "Cable junction";
  return "Passive mult";
}

function UtilityForm({
  utility,
  index,
  onDone,
}: {
  utility: UtilityNode | null;
  index: number;
  onDone: () => void;
}) {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const createUtility = useEditorStore((state) => state.createUtility);
  const updateUtility = useEditorStore((state) => state.updateUtility);
  const deleteUtility = useEditorStore((state) => state.deleteUtility);
  const initialKind = (utility?.utilityType ?? "passive-mult") as UtilityKind;
  const [kind, setKind] = useState<UtilityKind>(initialKind);
  const [name, setName] = useState(utility?.displayName ?? defaultName(initialKind));
  const [preset, setPreset] = useState<MultPreset>("1-to-3");
  const [splitterPreset, setSplitterPreset] = useState<SplitterPreset>("two-branch");
  const [manufacturer, setManufacturer] = useState(utility?.manufacturer ?? "");
  const [model, setModel] = useState(utility?.model ?? "");
  const [notes, setNotes] = useState(utility?.userNotes ?? "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const displayName = name.trim();
    if (!displayName) return;
    if (utility) {
      updateUtility(utility.id, {
        displayName,
        manufacturer: manufacturer.trim() || undefined,
        model: model.trim() || undefined,
        userNotes: notes.trim(),
      });
    } else {
      const attenuator = kind.includes("attenuator");
      const value: UtilityCreate = {
        utilityType: kind,
        displayName,
        position: defaultUtilityPosition(index, activeDocument),
        scale: 1,
        ports: attenuator
          ? attenuatorPorts(kind as "standalone-attenuator" | "inline-attenuator")
          : kind === "splitter-cable"
            ? splitterPorts(splitterPreset)
            : kind === "stackable-junction"
              ? junctionPorts()
              : multPorts(preset),
        controls: attenuator
          ? [
              {
                id: "level",
                label: "Level",
                type: "knob",
                position: {
                  x: kind === "inline-attenuator" ? 90 : 110,
                  y: kind === "inline-attenuator" ? 68 : 76,
                },
                value: 1,
              },
            ]
          : [],
        visualStyle:
          kind === "inline-attenuator"
            ? "inline"
            : kind === "standalone-attenuator"
              ? "module"
              : "compact",
        manufacturer: manufacturer.trim() || undefined,
        model: model.trim() || undefined,
        userNotes: notes.trim(),
      };
      createUtility(value);
    }
    onDone();
  };

  const changeKind = (nextKind: UtilityKind) => {
    setName((current) => (current === defaultName(kind) ? defaultName(nextKind) : current));
    setKind(nextKind);
  };

  return (
    <form className="external-endpoint-form" onSubmit={submit}>
      {!utility && (
        <label>
          <span>Utility type</span>
          <select onChange={(event) => changeKind(event.target.value as UtilityKind)} value={kind}>
            <option value="passive-mult">Passive mult</option>
            <option value="splitter-cable">Splitter or multi-head cable</option>
            <option value="stackable-junction">Movable four-ended cable junction</option>
            <option value="standalone-attenuator">Standalone attenuator</option>
            <option value="inline-attenuator">Inline attenuator</option>
          </select>
        </label>
      )}
      <label>
        <span>Label</span>
        <input autoFocus onChange={(event) => setName(event.target.value)} value={name} />
      </label>
      {!utility && kind === "splitter-cable" && (
        <label>
          <span>Branch layout</span>
          <select
            onChange={(event) => setSplitterPreset(event.target.value as SplitterPreset)}
            value={splitterPreset}
          >
            <option value="two-branch">Y splitter · two branches</option>
            <option value="three-branch">Multi-head · three branches</option>
          </select>
        </label>
      )}
      {!utility && kind === "passive-mult" && (
        <label>
          <span>Socket layout</span>
          <select onChange={(event) => setPreset(event.target.value as MultPreset)} value={preset}>
            <option value="1-to-3">1-to-3 · four common sockets</option>
            <option value="1-to-4">1-to-4 · five common sockets</option>
            <option value="2x-1-to-3">2 × 1-to-3 · two isolated groups</option>
          </select>
        </label>
      )}
      <div className="external-endpoint-form-grid">
        <label>
          <span>Manufacturer</span>
          <input onChange={(event) => setManufacturer(event.target.value)} value={manufacturer} />
        </label>
        <label>
          <span>Model</span>
          <input onChange={(event) => setModel(event.target.value)} value={model} />
        </label>
      </div>
      <label>
        <span>Notes</span>
        <textarea onChange={(event) => setNotes(event.target.value)} rows={2} value={notes} />
      </label>
      <div className="external-endpoint-form-actions">
        {utility && (
          <button
            className="is-danger"
            onClick={() => {
              deleteUtility(utility.id);
              onDone();
            }}
            type="button"
          >
            <Icon name="trash" size={14} /> Delete
          </button>
        )}
        <button onClick={onDone} type="button">
          Cancel
        </button>
        <button disabled={!name.trim()} type="submit">
          <Icon name={utility ? "save" : "plus"} size={14} />
          {utility ? "Save utility" : "Add utility"}
        </button>
      </div>
    </form>
  );
}

function utilitySummary(utility: UtilityNode, connectedPortIds: Set<string>) {
  if (utility.utilityType.includes("attenuator")) {
    const level = utility.controls.find((control) => control.id === "level")?.value ?? 1;
    return `${Math.round(level * 100)}% level · directional`;
  }
  if (utility.utilityType === "splitter-cable" || utility.utilityType === "stackable-junction") {
    const connected = utility.ports.filter((port) =>
      connectedPortIds.has(`utility:${utility.id}:${port.id}`),
    ).length;
    return `${connected}/${utility.ports.length} ends connected · ${
      utility.ports.length - connected
    } unused`;
  }
  return `${utility.ports.length} sockets · ${new Set(utility.ports.map((port) => port.groupId)).size} groups`;
}

export function UtilityManager() {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const select = useEditorStore((state) => state.select);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const utilities = useMemo(() => utilityNodes(activeDocument), [activeDocument]);
  const connectedPortIds = useMemo(
    () =>
      new Set(
        activeDocument.workspace.cableAssemblies.flatMap((cable) =>
          cableEndpoints(cable)
            .filter((endpoint) => endpoint.kind === "port")
            .map((endpoint) => (endpoint.kind === "port" ? endpoint.portId : "")),
        ),
      ),
    [activeDocument.workspace.cableAssemblies],
  );
  const editingUtility =
    editingId && editingId !== "new"
      ? (utilities.find((utility) => utility.id === editingId) ?? null)
      : null;

  return (
    <section
      aria-label="Patch utilities"
      className={`external-endpoint-manager utility-manager${open ? " is-open" : ""}`}
    >
      <button
        aria-expanded={open}
        className="external-endpoint-toggle"
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <Icon name="cable" size={16} />
        <span>Utilities</span>
        <b>{utilities.length}</b>
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="external-endpoint-body">
          <header>
            <div>
              <span className="eyebrow">Workspace</span>
              <h2>Patch utilities</h2>
            </div>
            <div>
              <button aria-label="Add utility" onClick={() => setEditingId("new")} type="button">
                <Icon name="plus" size={15} />
              </button>
              <button
                aria-label="Close patch utilities"
                onClick={() => setOpen(false)}
                type="button"
              >
                <Icon name="close" size={15} />
              </button>
            </div>
          </header>
          {editingId ? (
            <UtilityForm
              index={utilities.length}
              key={editingUtility?.id ?? "new"}
              onDone={() => setEditingId(null)}
              utility={editingUtility}
            />
          ) : utilities.length ? (
            <ul className="external-endpoint-list">
              {utilities.map((utility) => (
                <li key={utility.id}>
                  <button
                    onClick={() => {
                      select(utility.id);
                      setEditingId(utility.id);
                    }}
                    type="button"
                  >
                    <span>
                      <strong>{utility.displayName}</strong>
                      <small>{utilitySummary(utility, connectedPortIds)}</small>
                    </span>
                    <em>{utility.model ?? utility.utilityType}</em>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="external-endpoint-empty">
              <p>Add physical mults and attenuators to the documented patch.</p>
              <button onClick={() => setEditingId("new")} type="button">
                <Icon name="plus" size={14} /> Add utility
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
